import React, { useMemo } from "react";
import {addDays, differenceInCalendarDays, format} from "date-fns";
import { FormattedMessage } from "react-intl";
import { DailySet, HandleInputChangeRequest, ValueTypes } from "../../types";
import { Input } from "../input";
import { RightArrow } from "../assets/RightArrow";
import { QuickUpdateColumn, TickerColumn, TypeColumn, ChainContainer, ArrowContainer } from "../styles";
import { messages } from "./messages";
import { DATE_FORMAT, NAME_TYPE } from "../../constants";
import { Chain } from "../assets/Chain";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { formatDefaultDate } from "../../../helpers/dates";
import { AriType } from "../../../updateForm";

interface Props {
  ticker: string;
  name?: string;
  parentTicker?: string;
  dailySet: DailySet[];
  valueType: ValueTypes;
  type: AriType;
  handleQuickUpdate: (
    ticker: string,
    valueType: ValueTypes,
    type: AriType
  ) => void;
  enableEdition: boolean;
  handleInputChange: (props: HandleInputChangeRequest) => void;
  isTickerVisible: boolean;
  isComplement?: boolean;
  nameType?: NAME_TYPE;
  disabled?: boolean;
  updatedARIValues?: { [key: string]: number | boolean };
  formula?: string;
  showAltStyles?: boolean;
  inputWidth: number;
}

const getInputKey = (ticker: string, type: string, startDate: string, index: number): string =>
  `${ticker}_${type}_${format(addDays(new Date(startDate), index), DATE_FORMAT.DEFAULT)}`;

const getInputType = (valueType: string): "text" | "checkbox" => {
  switch (valueType) {
    case ValueTypes.closed:
    case ValueTypes.closeToArrival:
    case ValueTypes.closeToDeparture:
      return "checkbox";

    default:
      return "text";
  }
}

export const Row = (
  {
    ticker,
    name,
    parentTicker,
    dailySet,
    valueType,
    type,
    handleQuickUpdate,
    enableEdition,
    handleInputChange,
    isTickerVisible,
    isComplement,
    nameType,
    disabled,
    updatedARIValues,
    formula,
    showAltStyles,
    inputWidth,
  }: Props,
): React.ReactElement => {
  const displayName = useMemo(() =>
    nameType === NAME_TYPE.FULL && name ? name : ticker,
    [name, nameType, ticker]
  );

  const tooltipText = useMemo(() => parentTicker || formula, [parentTicker, formula]);
  const isAltStyleAvailability = useMemo(() => showAltStyles && valueType === ValueTypes.availability, [showAltStyles, valueType]);
  const isAltStyleClosed = useMemo(() => showAltStyles && valueType === ValueTypes.closed, [showAltStyles, valueType]);

  const NameCell = useMemo(() => {
    if (isTickerVisible) {
      return (
        <OverlayTrigger
          delay={{ show: 250, hide: 0 }}
          overlay={
            <Tooltip id={displayName}>
              {displayName}
            </Tooltip>
          }
        >
          <TickerColumn visible={isTickerVisible} disabled={disabled} lite={showAltStyles}>
            {displayName}
          </TickerColumn>
        </OverlayTrigger>
      );
    }

    return <TickerColumn disabled={disabled} />;
  }, [isTickerVisible, displayName, disabled]);

  return (
    <>
      {NameCell}
      <TypeColumn disabled={!!parentTicker || !!formula} lite={showAltStyles}>
        {isComplement
          ? <FormattedMessage {...messages.value} />
          : <FormattedMessage {...messages[valueType as keyof typeof messages]} />
        }
      </TypeColumn>

      {(parentTicker || formula) && tooltipText && (
        <OverlayTrigger
          delay={{ show: 250, hide: 0 }}
          overlay={
            <Tooltip id={ticker}>
              {tooltipText}
            </Tooltip>
          }
        >
          <ChainContainer
            onClick={() => navigator.clipboard.writeText(tooltipText)}
            lite={showAltStyles}
            disabled={!!parentTicker || !!formula}
          >
            <Chain />
          </ChainContainer>
        </OverlayTrigger>
      )}

      {!parentTicker && !formula && (
        <QuickUpdateColumn onClick={() => handleQuickUpdate(ticker, valueType, type)}>
          <ArrowContainer>
            <RightArrow />
            <RightArrow />
          </ArrowContainer>
        </QuickUpdateColumn>
      )}

      {dailySet?.map(dailySetValues => {
        const { startDate, endDate, value } = dailySetValues;
        const days = differenceInCalendarDays(new Date(endDate), new Date(startDate)) + 1;

        return Array.from({ length: days }, (element, idx) => {
          const date = formatDefaultDate(addDays(new Date(startDate), idx));
          const defaultValue = updatedARIValues && typeof updatedARIValues[date] !== "undefined"
            ? updatedARIValues[date]
            : value;
          return (
            <Input
              key={getInputKey(ticker, valueType, startDate, idx)}
              ticker={ticker}
              isDisabled={!!parentTicker || !!formula}
              valueType={valueType}
              type={type}
              defaultValue={defaultValue}
              inputType={getInputType(valueType)}
              enableEdition={enableEdition}
              date={addDays(new Date(startDate), idx)}
              handleInputChange={handleInputChange}
              isZero={Boolean(isAltStyleAvailability && defaultValue === 0)}
              red={Boolean(isAltStyleClosed && defaultValue)}
              bold={Boolean(showAltStyles && !parentTicker)}
              inputWidth={inputWidth}
            />
          );
        });
      })}
    </>
  );
};
